// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Box from "../layout-and-structure/Box.res.js";
import * as I18n from "../../primitives/I18n.res.js";
import * as React from "react";
import * as Inline from "../layout-and-structure/Inline.res.js";
import * as Spinner from "../feedback-indicators/Spinner.res.js";
import * as FontFaces from "../theme/FontFaces.res.js";
import * as TextStyle from "../typography/TextStyle.res.js";
import * as LegacyIcon from "../images-and-icons/LegacyIcon.res.js";
import * as ReactAria from "react-aria";
import * as ReactStately from "../../externals/ReactStately.res.js";
import * as ReactDOMStyle from "@rescript/react/src/ReactDOMStyle.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as AnimatedRender from "../../primitives/AnimatedRender.res.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as ReactDomEventTarget from "../../primitives/ReactDomEventTarget.res.js";
import * as UseResizeObserver from "react-aria/private/utils/useResizeObserver";

let defaultSectionTitle = I18n.t("Results");

function backgroundColor(focused, selected, disabled) {
  if (disabled) {
    return "#f3f3f7";
  } else if (focused) {
    return "#f4e4ee";
  } else {
    return "transparent";
  }
}

function fontColor(focused, selected, disabled) {
  if (disabled) {
    return "#8c8B9b";
  } else if (selected || focused) {
    return "#a01e72";
  } else {
    return "#25243a";
  }
}

let noResultItemStyle = {
  backgroundColor: "transparent",
  color: "#797885",
  cursor: "default",
  fontFamily: FontFaces.fontFamilyFromFontName("LibreFranklin"),
  fontSize: "15px",
  fontStyle: "italic",
  lineHeight: "35px",
  paddingRight: "10px",
  paddingLeft: "10px",
  textAlign: "center"
};

function itemStyle(focused, selected, disabled, size) {
  return {
    backgroundColor: backgroundColor(focused, selected, disabled),
    color: fontColor(focused, selected, disabled),
    cursor: disabled ? "default" : "pointer",
    display: "flex",
    fontFamily: FontFaces.fontFamilyFromFontName("LibreFranklin"),
    fontSize: size === "compact" ? "14px" : "15px",
    fontWeight: selected ? "500" : "400",
    lineHeight: size === "compact" ? "20px" : "22px",
    marginTop: "1px",
    marginBottom: "1px",
    minHeight: size === "compact" ? "32px" : "35px",
    paddingRight: "10px",
    paddingLeft: "10px",
    borderRadius: size === "compact" ? "3px" : "5px",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  };
}

let listBottomGradientStyle = {
  backgroundImage: "radial-gradient(ellipse at center, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 75%)",
  backgroundPosition: "center top",
  bottom: "0",
  height: "16px",
  left: "0",
  position: "absolute",
  right: "0",
  zIndex: "1",
  backgroundSize: "100% 200%",
  pointerEvents: "none"
};

let sectionStyle = {
  display: "flex",
  overflow: "hidden",
  flexDirection: "column"
};

function listStyle(size) {
  return ReactDOMStyle.unsafeAddProp({
    display: "flex",
    maxHeight: "240px",
    overflow: "auto",
    padding: size === "compact" ? "2px 3px" : "3px 4px",
    flexDirection: "column"
  }, "scrollPaddingBlock", "5px");
}

let make = React.memo(props => {
  let size = props.size;
  let item = props.item;
  let optionRef = React.useRef(null);
  let match = ReactAria.useOption({
    key: item.key
  }, props.state, optionRef);
  let disabled = match.isDisabled;
  let selected = match.isSelected;
  let newrecord = {...match.optionProps};
  return JsxRuntime.jsxs("div", (newrecord.style = itemStyle(match.isFocused, selected, disabled, size), newrecord.ref = Primitive_option.some(optionRef), newrecord.children = [
    JsxRuntime.jsx(Box.make, {
      children: item.rendered,
      spaceY: "xsmall",
      grow: true
    }),
    JsxRuntime.jsx("div", {
      children: selected ? JsxRuntime.jsx(LegacyIcon.make, {
          name: "tick_bold",
          fill: disabled ? "#bdbdca" : "#c04695",
          size: size === "compact" ? 12 : 14
        }) : null,
      style: {
        display: "flex",
        minWidth: "14px",
        paddingLeft: "12px",
        boxSizing: "content-box"
      }
    })
  ], newrecord));
});

let make$1 = React.memo(props => {
  let size = props.size;
  let state = props.state;
  let section = props.section;
  let match = ReactAria.useListBoxSection({
    heading: section.rendered,
    "aria-label": "section"
  });
  let children = ReactStately.Collection.toArray(section.childNodes);
  let itemsLimit = Stdlib_Option.getOr(props.itemsLimit, children.length);
  let childElements = children.slice(0, itemsLimit);
  let newrecord = {...match.itemProps};
  let tmp;
  if (section.rendered !== null && ReactStately.Collection.toArray(section.childNodes).length !== 0) {
    let newrecord$1 = {...match.headingProps};
    tmp = JsxRuntime.jsx(Box.make, {
      children: JsxRuntime.jsx(TextStyle.make, {
        children: JsxRuntime.jsx("span", (newrecord$1.children = section.rendered, newrecord$1)),
        variation: "normal",
        size: "xxsmall"
      }),
      spaceTop: "xnormal",
      spaceBottom: "xsmall",
      spaceLeft: "xnormal"
    });
  } else {
    tmp = null;
  }
  let newrecord$2 = {...match.groupProps};
  newrecord.children = [
    tmp,
    JsxRuntime.jsx("div", (newrecord$2.style = sectionStyle, newrecord$2.children = childElements.map(item => JsxRuntime.jsx(make, {
      item: item,
      state: state,
      size: size
    }, item.key)), newrecord$2))
  ];
  return JsxRuntime.jsxs("div", newrecord);
});

function ListBox(props) {
  let onScroll = props.onScroll;
  let __footerElement = props.footerElement;
  let __loading = props.loading;
  let noResultLabel = props.noResultLabel;
  let itemsLimit = props.itemsLimit;
  let __size = props.size;
  let state = props.state;
  let domRef = props.domRef;
  let size = __size !== undefined ? __size : "normal";
  let loading = __loading !== undefined ? __loading : false;
  let footerElement = __footerElement !== undefined ? __footerElement : null;
  let match = React.useState(() => false);
  let setScrollable = match[1];
  let match$1 = React.useState(() => false);
  let setScrollReachedBottom = match$1[1];
  let match$2 = ReactAria.useListBox(props.props, state, domRef);
  let handleScrollable = React.useCallback(() => {
    let domElement = domRef.current;
    if (!(domElement == null)) {
      return setScrollable(param => domElement.scrollHeight > domElement.clientHeight);
    }
  }, [domRef]);
  React.useLayoutEffect(() => {
    handleScrollable();
  }, [
    handleScrollable,
    domRef
  ]);
  UseResizeObserver.useResizeObserver({
    ref: domRef,
    onResize: handleScrollable
  });
  let onScroll$1 = React.useCallback(event => {
    let domElement = ReactDomEventTarget.toUnsafeDomEventTarget(event.currentTarget);
    let scrollBottom = (domElement.scrollHeight - (domElement.scrollTop | 0) | 0) - domElement.clientHeight | 0;
    setScrollable(param => scrollBottom > 0);
    setScrollReachedBottom(param => scrollBottom < 15);
    Stdlib_Option.forEach(onScroll, fn => fn(event));
  }, []);
  let noItemPlaceholder = JsxRuntime.jsx("span", {
    children: noResultLabel !== undefined ? noResultLabel : I18n.t("No result found"),
    style: noResultItemStyle
  });
  let newrecord = {...match$2.listBoxProps};
  newrecord.style = {
    position: "contents"
  };
  let collection = ReactStately.Collection.toArray(state.collection);
  let tmp;
  let exit = 0;
  if (loading) {
    tmp = JsxRuntime.jsx(Inline.make, {
      children: JsxRuntime.jsx(Box.make, {
        children: JsxRuntime.jsx(Spinner.make, {
          size: size === "compact" ? 19 : 24
        }),
        spaceY: size === "compact" ? "xsmall" : "small"
      }),
      align: "center"
    });
  } else {
    let len = collection.length;
    if (len !== 1) {
      if (len !== 0) {
        exit = 1;
      } else {
        tmp = noItemPlaceholder;
      }
    } else {
      let section = collection[0];
      if (ReactStately.Collection.toArray(section.childNodes).length === 0) {
        tmp = noItemPlaceholder;
      } else {
        exit = 1;
      }
    }
  }
  if (exit === 1) {
    tmp = collection.map(section => JsxRuntime.jsx(make$1, {
      section: section,
      state: state,
      size: size,
      itemsLimit: itemsLimit
    }, section.key));
  }
  newrecord.children = [
    JsxRuntime.jsx("div", {
      children: JsxRuntime.jsxs("div", {
        children: [
          tmp,
          JsxRuntime.jsx(AnimatedRender.make, {
            children: JsxRuntime.jsx("div", {
              style: listBottomGradientStyle
            }),
            displayed: match[0] && !match$1[0],
            animation: "fade",
            duration: 250
          })
        ],
        ref: Primitive_option.some(domRef),
        style: listStyle(size),
        onScroll: onScroll$1
      }),
      style: {
        position: "relative"
      }
    }),
    footerElement
  ];
  return JsxRuntime.jsxs("div", newrecord);
}

let make$2 = React.memo(ListBox);

let defaultItemsPerScroll = 20;

export {
  defaultSectionTitle,
  defaultItemsPerScroll,
  make$2 as make,
}
/* defaultSectionTitle Not a pure module */
